import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Alert, AuthCard, Button } from '@sentinel/ui';
import { sanitizeReturnTo } from '@sentinel/auth-core';
import { RecoveryChallengeStep } from '../features/mfa/components/recovery-challenge-step';
import { RecoveryCodesStep } from '../features/mfa/components/recovery-codes-step';
import { useRecovery } from '../features/mfa/use-recovery';

// Dedicated recovery-code page used when the user has lost their MFA device
// and wants to re-enroll. Distinct from the inline recovery tab on
// /mfa/verify (which sends them on to `return_to`); this flow lands them at
// /mfa/setup so they re-enrol immediately. Both paths hit the same backend
// endpoint (POST /auth/mfa/recovery).

const searchSchema = z.object({
  token: z.string().optional(),
  return_to: z.string().optional(),
});

interface ErrorBody {
  error?: string;
  message?: string;
}
function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}
function getBody(e: unknown): ErrorBody | undefined {
  if (e && typeof e === 'object') {
    const maybe = e as { body?: ErrorBody } & ErrorBody;
    return maybe.body ?? maybe;
  }
  return undefined;
}

// Same backend phrase used by /mfa/verify — DomainError::Unauthorized with
// "invalid or expired MFA challenge token" surfaces a fatal expired-link
// state. Other 401s are inline "wrong recovery code" errors.
function isInvalidMfaToken(status: number | undefined, body: ErrorBody | undefined) {
  if (status !== 401) return false;
  const text = `${body?.error ?? ''} ${body?.message ?? ''}`.toLowerCase();
  return text.includes('invalid or expired mfa');
}

type Step = 'enter_code' | 'success';

function MfaRecoveryPage() {
  const { t } = useTranslation('mfa');
  const search = Route.useSearch();
  const mfaToken = search.token ?? null;

  const [step, setStep] = useState<Step>('enter_code');
  const [topError, setTopError] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const recovery = useRecovery(mfaToken);

  async function onSubmit(recoveryCode: string) {
    setInlineError(null);
    setTopError(null);
    try {
      const result = await recovery.mutateAsync({ recovery_code: recoveryCode });
      // Server has already set HttpOnly auth cookies. The user is logged in.
      setNewCodes(result.new_recovery_codes ?? []);
      setStep('success');
    } catch (e) {
      const status = getStatus(e);
      const body = getBody(e);
      if (isInvalidMfaToken(status, body)) {
        setTokenInvalid(true);
        return;
      }
      if (status === 401 || status === 400 || status === 409) {
        setInlineError(t('verify_invalid_recovery_code'));
        return;
      }
      setTopError(t('verify_generic_error'));
    }
  }

  function continueToSetup() {
    // After recovery the user is authenticated; /mfa/setup uses the session
    // flow. Forward a sanitized ?return_to so the post-enroll redirect lands
    // where the user originally wanted to go.
    const safeReturnTo = sanitizeReturnTo(search.return_to);
    const params = new URLSearchParams();
    if (safeReturnTo !== '/') params.set('return_to', safeReturnTo);
    const qs = params.toString();
    window.location.assign(`/mfa/setup${qs ? `?${qs}` : ''}`);
  }

  // Missing token → render the same expired-link state as /mfa/verify.
  if (!mfaToken || tokenInvalid) {
    return (
      <AuthCard title={t('recovery_invalid_token_title')}>
        <Alert type="error" role="alert">
          {t('recovery_invalid_token_message')}
        </Alert>
        <p>
          <Link to="/login">{t('return_to_login')}</Link>
        </p>
      </AuthCard>
    );
  }

  if (step === 'success') {
    return (
      <AuthCard title={t('recovery_success_title')}>
        <Alert type="success" role="status">
          {t('recovery_success_message')}
        </Alert>
        {newCodes.length > 0 ? (
          <RecoveryCodesStep
            codes={newCodes}
            title={t('recovery_new_codes_title')}
            subtitle={t('recovery_new_codes_warning')}
            statusMessage={null}
            doneLabel={t('recovery_continue_setup')}
            onDone={continueToSetup}
          />
        ) : (
          <Button
            type="button"
            data-testid="mfa-recovery-continue"
            onClick={continueToSetup}
          >
            {t('recovery_continue_setup')}
          </Button>
        )}
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('recovery_page_title')} subtitle={t('recovery_page_subtitle')}>
      {topError ? (
        <Alert type="error" role="alert">
          {topError}
        </Alert>
      ) : null}
      <RecoveryChallengeStep
        onSubmit={onSubmit}
        error={inlineError}
        submitting={recovery.isPending}
      />
    </AuthCard>
  );
}

export const Route = createFileRoute('/mfa/recovery')({
  validateSearch: searchSchema,
  component: MfaRecoveryPage,
});
